import Axios from 'axios';
import { BaseAPI } from './base-api';

export interface Comment {
  id: string;
  entityType: string;
  entityId: string;
  content: string;
  authorId: number;
  authorName: string;
  authorAvatar?: string;
  parentId?: string | null;
  visibilityScope: 'PUBLIC' | 'INTERNAL' | 'RESTRICTED';
  isEdited: boolean;
  isSystemComment: boolean;
  createdAt: string;
  updatedAt: string;
  attachments?: Attachment[];
}

export interface Attachment {
  id: string;
  assetId: number;
  fileName: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
}

export interface CommentsListResponse {
  comments: Comment[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListCommentsParams {
  entityType: string;
  entityId: string;
  limit?: number;
  offset?: number;
}

export interface CreateCommentData {
  entityType: string;
  entityId: string;
  content: string;
  parentId?: string;
  visibilityScope?: 'PUBLIC' | 'INTERNAL';
  attachmentIds?: number[];
}

export class CommentsAPI {
  static async list(params: ListCommentsParams): Promise<CommentsListResponse> {
    const response = await BaseAPI.request('GET', '/v1/comments', null, null, 'application/json', params);

    if (response?.status === 200) {
      // Backend returns {data: [], meta: {total, limit, offset}}
      // Transform to {comments: [], total, limit, offset}
      const { data, meta } = response.data;
      return {
        comments: data || [],
        total: meta?.total || 0,
        limit: meta?.limit || params.limit || 20,
        offset: meta?.offset || params.offset || 0
      };
    }

    throw new Error(response?.message || 'Failed to fetch comments');
  }

  static async create(data: CreateCommentData): Promise<Comment> {
    const response = await BaseAPI.request('POST', '/v1/comments', data);

    if (response?.status === 201 || response?.status === 200) {
      return response.data;
    }

    throw new Error(response?.message || 'Failed to create comment');
  }

  static async delete(id: string): Promise<void> {
    const response = await BaseAPI.request('DELETE', `/v1/comments/${id}`);

    if (response?.status !== 200) {
      throw new Error(response?.message || 'Failed to delete comment');
    }
  }
}

export interface AssetResponse {
  asset: {
    assetId: string;
    url: string;
    downloadUrl: string;
    contentType: string;
    id: number;
    title: string;
  };
}

export class AssetAPI {
  static async getSignedUrl(assetId: number, source?: string): Promise<AssetResponse> {
    const params: Record<string, any> = {};
    if (source) {
      params.source = source;
    }
    const response = await BaseAPI.request('GET', `/asset/${assetId}/get-signed-url`, null, null, 'application/json', params);

    if (response?.status === 200) {
      return response.data;
    }

    throw new Error(response?.message || 'Failed to get signed URL');
  }

  static async upload(file: File): Promise<number> {
    // 1. Request signed URL and asset ID
    const uploadData = {
      title: file.name,
      contentType: file.type,
      fileSize: file.size,
      uploadFolder: 'comments' // Default folder for comment attachments
    };

    const result = await BaseAPI.request('POST', '/asset', uploadData);

    if (result.status !== 200 && result.status !== 201) {
      throw new Error(result.message || 'Failed to initialize file upload');
    }

    const { signedUrl, asset } = result.data;

    // 2. Upload to S3 via PUT
    await Axios.put(signedUrl, file, {
      headers: {
        'Content-Type': file.type
      }
    });

    // 3. Return the numeric asset.id for linking with the comment
    return asset.id;
  }

  /**
   * Upload a file as a task attachment.
   * Uses category='task_attachment' and project=taskId for filtering.
   */
  static async uploadTaskAttachment(file: File, taskId: string): Promise<number> {
    const uploadData = {
      title: file.name,
      contentType: file.type,
      fileSize: file.size,
      uploadFolder: 'task-documents',
      category: 'task_attachment',
      project: taskId,
    };

    const result = await BaseAPI.request('POST', '/asset', uploadData);

    if (result.status !== 200 && result.status !== 201) {
      throw new Error(result.message || 'Failed to initialize file upload');
    }

    const { signedUrl, asset } = result.data;

    await Axios.put(signedUrl, file, {
      headers: { 'Content-Type': file.type }
    });

    // System comment for attachment activity is now created server-side by the asset API
    return asset.id as number;
  }

  /**
   * List all attachments for a task via the paginated asset endpoint.
   */
  static async getTaskAttachments(taskId: string): Promise<TaskAttachment[]> {
    const response = await BaseAPI.request(
      'GET', '/asset/paginated', null, null, 'application/json',
      { category: 'task_attachment', projectName: taskId, limit: 50 }
    );

    if (response?.status === 200) {
      // Response shape: { data: AssetEntity[], total, page, limit }
      const items = Array.isArray(response.data?.data) ? response.data.data
        : Array.isArray(response.data) ? response.data
        : [];
      return items.map((item: any) => ({
        id: item.id,
        title: item.title || item.name || 'Untitled',
        contentType: item.contentType || item.content_type || '',
        fileSize: item.fileSize || item.file_size || 0,
        createdAt: item.createdAt || item.created_at || '',
        uploadedBy: item.user?.firstName
          ? `${item.user.firstName} ${item.user.lastName || ''}`.trim()
          : item.createdBy || '',
        uploadedByUserId: item.user?.id ? Number(item.user.id) : undefined,
      }));
    }

    // Return empty array instead of throwing — no attachments is not an error
    return [];
  }

  /**
   * Archive (soft-remove) a task attachment.
   */
  static async archiveTaskAttachment(assetId: number, taskId?: string, fileName?: string): Promise<void> {
    const response = await BaseAPI.request('PATCH', `/asset/${assetId}/archive`);
    if (response?.status !== 200) {
      throw new Error(response?.data?.message || response?.message || 'Failed to remove attachment');
    }
    // System comment for removal is now created server-side by the archive API
  }
}

export interface TaskAttachment {
  id: number;
  title: string;
  contentType: string;
  fileSize: number;
  createdAt: string;
  uploadedBy: string;
  uploadedByUserId?: number;
}
